import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../config/supabase';
import { useFarm } from '../contexts/FarmContext';
import { 
  Building2, Plus, Users, Tractor, Search, X, Check, Edit2, Trash2,
  ChevronDown, ChevronUp, Mail, Phone, FileText, Shield, ShieldCheck,
  UserPlus, ToggleLeft, ToggleRight, Eye, EyeOff, RefreshCw, UserCog
} from 'lucide-react';

// ─── Main Component ──────────────────────────────────────────────────────────
const OrganizationManagement = () => {
  const { allFarms, refreshFarms } = useFarm();
  
  // Auth state
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null); // 'admin' | 'comp_admin' | null
  const [userOrgId, setUserOrgId] = useState(null); // org id if comp_admin
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data state
  const [organizations, setOrganizations] = useState([]);
  const [orgTypes, setOrgTypes] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [expandedOrg, setExpandedOrg] = useState(null);
  const [orgSearch, setOrgSearch] = useState('');

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignUsersModal, setShowAssignUsersModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [saving, setSaving] = useState(false);

  // ─── Auth & Role Check ───────────────────────────────────────────────────
  useEffect(() => {
    checkUserRole();
  }, []);

  const checkUserRole = async () => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) { setLoading(false); return; }

      const { data: userData } = await supabase
        .from('user')
        .select('id, email, is_admin, name_first, name_last')
        .eq('email', authUser.email)
        .maybeSingle();

      if (!userData) { setLoading(false); return; }
      setCurrentUser(userData);

      if (userData.is_admin) {
        setUserRole('admin');
        loadData('admin', null);
      } else {
        // Check if user is comp_admin of any org
        const { data: userOrgs } = await supabase
          .from('user_org')
          .select('id, organisation, role')
          .eq('user', userData.id)
          .eq('role', 'comp_admin');

        if (userOrgs && userOrgs.length > 0) {
          setUserRole('comp_admin');
          setUserOrgId(userOrgs[0].organisation);
          loadData('comp_admin', userOrgs[0].organisation);
        } else {
          setUserRole(null);
          setLoading(false);
        }
      }
    } catch (err) {
      console.error('Error checking role:', err);
      setLoading(false);
    }
  };

  // ─── Load Data ───────────────────────────────────────────────────────────
  const loadData = async (role, orgId) => {
    setLoading(true);
    setError(null);
    try {
      // Load org types
      const { data: typesData } = await supabase
        .from('organisation_type')
        .select('*')
        .order('id');
      setOrgTypes(typesData || []);

      let orgsQuery = supabase.from('organisation').select('*').eq('org_type', 2).order('name_org');
      
      if (role === 'comp_admin' && orgId) {
        orgsQuery = supabase.from('organisation').select('*').eq('id', orgId);
      }

      const { data: orgsData, error: orgsError } = await orgsQuery;
      if (orgsError) throw orgsError;

      // Load counts for each org using the new RPC
      const orgsWithDetails = await Promise.all(
        (orgsData || []).map(async (org) => {
          const { data: orgUsers } = await supabase.rpc('get_org_users_with_roles', { p_org_id: org.id });
          const { data: farmCountData } = await supabase.rpc('count_org_farms', { org_id: org.id });
          
          const compAdmin = orgUsers?.find(u => u.role === 'comp_admin');
          
          return {
            ...org,
            farmCount: farmCountData || 0,
            userCount: orgUsers?.length || 0,
            activeUserCount: orgUsers?.filter(u => u.is_active).length || 0,
            compAdmin: compAdmin || null,
            users: orgUsers || []
          };
        })
      );

      setOrganizations(orgsWithDetails);

      // Load all users (admin only)
      if (role === 'admin') {
        const { data: usersData } = await supabase
          .from('user')
          .select('id, email, name_first, name_last, mobile')
          .order('email');
        setAllUsers(usersData || []);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const refresh = () => loadData(userRole, userOrgId);

  // ─── Filtered orgs ──────────────────────────────────────────────────────
  const filteredOrgs = useMemo(() => {
    if (!orgSearch.trim()) return organizations;
    const q = orgSearch.toLowerCase();
    return organizations.filter(o => 
      o.name_org?.toLowerCase().includes(q) ||
      o.email?.toLowerCase().includes(q)
    );
  }, [organizations, orgSearch]);

  // ─── Toggle user active status (comp_admin feature) ──────────────────────
  const handleToggleUserActive = async (userOrgId, currentActive) => {
    try {
      const { error } = await supabase.rpc('toggle_user_org_active', {
        p_user_org_id: userOrgId,
        p_is_active: !currentActive
      });
      if (error) throw error;
      refresh();
    } catch (err) {
      alert('Error toggling user status: ' + err.message);
    }
  };

  // ─── Delete org (admin only) ─────────────────────────────────────────────
  const handleDeleteOrg = async () => {
    if (!selectedOrg) return;
    setSaving(true);
    try {
      // Delete user_org_farm entries first, then user_org, then org
      const { data: userOrgs } = await supabase
        .from('user_org')
        .select('id')
        .eq('organisation', selectedOrg.id);
      
      if (userOrgs?.length > 0) {
        const uoIds = userOrgs.map(uo => uo.id);
        await supabase.from('user_org_farm').delete().in('user_org', uoIds);
        await supabase.from('user_org').delete().eq('organisation', selectedOrg.id);
      }
      
      const { error } = await supabase.from('organisation').delete().eq('id', selectedOrg.id);
      if (error) throw error;
      
      setShowDeleteConfirm(false);
      setSelectedOrg(null);
      refresh();
    } catch (err) {
      alert('Error deleting organization: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // ─── Access Denied ───────────────────────────────────────────────────────
  if (!loading && !userRole) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Access denied. Admin or Company Admin privileges required.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        <span className="ml-3 text-gray-600">Loading organizations...</span>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-7 h-7 text-green-600" />
            Organization Management
          </h1>
          <p className="text-gray-600 mt-1">
            {userRole === 'admin' ? 'Create and manage organizations, assign users and farms' : 'Manage your organization members and farms'}
          </p>
          <div className="flex items-center gap-2 mt-2">
            <span className={`px-2 py-0.5 text-xs rounded-full ${userRole === 'admin' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
              {userRole === 'admin' ? '🛡️ Platform Admin' : '🏢 Company Admin'}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={refresh} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg" title="Refresh">
            <RefreshCw className="w-5 h-5" />
          </button>
          {userRole === 'admin' && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Create Organization
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Search */}
      {userRole === 'admin' && organizations.length > 3 && (
        <div className="mb-4">
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={orgSearch}
              onChange={(e) => setOrgSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              placeholder="Search organizations..."
            />
          </div>
        </div>
      )}

      {/* Organizations List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Organizations ({filteredOrgs.length})
          </h2>
        </div>

        {filteredOrgs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No organizations found.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredOrgs.map((org) => (
              <OrgCard
                key={org.id}
                org={org}
                userRole={userRole}
                expanded={expandedOrg === org.id}
                onToggleExpand={() => setExpandedOrg(expandedOrg === org.id ? null : org.id)}
                onEdit={() => { setSelectedOrg(org); setShowEditModal(true); }}
                onDelete={() => { setSelectedOrg(org); setShowDeleteConfirm(true); }}
                onAssignUsers={() => { setSelectedOrg(org); setShowAssignUsersModal(true); }}
                onToggleUserActive={handleToggleUserActive}
                onRefresh={refresh}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create Organization Modal */}
      {showCreateModal && (
        <CreateOrgModal
          orgTypes={orgTypes}
          allUsers={allUsers}
          onClose={() => setShowCreateModal(false)}
          onCreated={refresh}
        />
      )}

      {/* Edit Organization Modal */}
      {showEditModal && selectedOrg && (
        <EditOrgModal
          org={selectedOrg}
          orgTypes={orgTypes}
          onClose={() => { setShowEditModal(false); setSelectedOrg(null); }}
          onSaved={refresh}
        />
      )}

      {/* Assign Users Modal (Admin only) */}
      {showAssignUsersModal && selectedOrg && userRole === 'admin' && (
        <AssignUsersModal
          org={selectedOrg}
          allUsers={allUsers}
          onClose={() => { setShowAssignUsersModal(false); setSelectedOrg(null); }}
          onSaved={() => { setShowAssignUsersModal(false); setSelectedOrg(null); refresh(); refreshFarms(); }}
        />
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && selectedOrg && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Organization</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete <strong>{selectedOrg.name_org}</strong>? 
              This will remove all user and farm assignments. This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => { setShowDeleteConfirm(false); setSelectedOrg(null); }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={handleDeleteOrg} disabled={saving}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">
                <Trash2 className="w-4 h-4" />
                {saving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Org Card Component ──────────────────────────────────────────────────────
const OrgCard = ({ org, userRole, expanded, onToggleExpand, onEdit, onDelete, onAssignUsers, onToggleUserActive, onRefresh }) => {
  return (
    <div className="p-4">
      <div className="flex items-center justify-between cursor-pointer" onClick={onToggleExpand}>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
            <Building2 className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{org.name_org || 'Unnamed'}</h3>
            <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
              <span className="flex items-center gap-1"><Tractor className="w-4 h-4" />{org.farmCount} farms</span>
              <span className="flex items-center gap-1"><Users className="w-4 h-4" />{org.activeUserCount}/{org.userCount} users</span>
              {org.compAdmin && (
                <span className="flex items-center gap-1 text-blue-600">
                  <ShieldCheck className="w-4 h-4" />
                  {org.compAdmin.name_first || org.compAdmin.email}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {userRole === 'admin' && (
            <>
              <button onClick={(e) => { e.stopPropagation(); onAssignUsers(); }}
                className="px-3 py-1.5 text-sm bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 flex items-center gap-1">
                <UserPlus className="w-4 h-4" /> Assign Users
              </button>
              <button onClick={(e) => { e.stopPropagation(); onEdit(); }}
                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                <Edit2 className="w-4 h-4" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); onDelete(); }}
                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                <Trash2 className="w-4 h-4" />
              </button>
            </>
          )}
          {expanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="mt-4 pl-16">
          {/* Org Info */}
          <div className="grid grid-cols-2 gap-3 text-sm mb-4">
            {org.email && <div className="flex items-center gap-2 text-gray-600"><Mail className="w-4 h-4" />{org.email}</div>}
            {org.contact && <div className="flex items-center gap-2 text-gray-600"><Phone className="w-4 h-4" />{org.contact}</div>}
            {org.company_registration_no && <div className="flex items-center gap-2 text-gray-600"><FileText className="w-4 h-4" />Reg: {org.company_registration_no}</div>}
          </div>

          {/* Users List with Active Toggle */}
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Users className="w-4 h-4" /> Members ({org.users?.length || 0})
            </h4>
            {org.users && org.users.length > 0 ? (
              <div className="space-y-2">
                {org.users.map(u => (
                  <div key={u.user_org_id} className={`flex items-center justify-between p-2 rounded-lg text-sm ${u.is_active ? 'bg-gray-50' : 'bg-red-50 opacity-60'}`}>
                    <div className="flex items-center gap-2">
                      {u.role === 'comp_admin' ? (
                        <ShieldCheck className="w-4 h-4 text-blue-600" />
                      ) : (
                        <Users className="w-4 h-4 text-gray-400" />
                      )}
                      <span className={u.is_active ? 'text-gray-800' : 'text-gray-500 line-through'}>
                        {[u.name_first, u.name_last].filter(Boolean).join(' ') || u.email}
                      </span>
                      {u.role === 'comp_admin' && (
                        <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">Admin</span>
                      )}
                      <span className="text-gray-400 text-xs">{u.email}</span>
                      <span className="text-gray-400 text-xs">({u.farm_count} farms)</span>
                    </div>
                    {/* Toggle active - comp_admin can toggle members, admin can toggle all except comp_admin */}
                    {u.role !== 'comp_admin' && (
                      <button
                        onClick={() => onToggleUserActive(u.user_org_id, u.is_active)}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                          u.is_active 
                            ? 'text-green-700 hover:bg-green-100' 
                            : 'text-red-700 hover:bg-red-100'
                        }`}
                        title={u.is_active ? 'Deactivate from org view' : 'Reactivate in org view'}
                      >
                        {u.is_active ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                        {u.is_active ? 'Active' : 'Inactive'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No members assigned</p>
            )}
          </div>

          {/* Farms summary */}
          <OrgFarmsList orgId={org.id} />
        </div>
      )}
    </div>
  );
};

// ─── Org Farms List (lazy loaded) ────────────────────────────────────────────
const OrgFarmsList = ({ orgId }) => {
  const [farms, setFarms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.rpc('get_farms_by_org_via_user_org_farm', { org_id: orgId });
      setFarms(data || []);
      setLoading(false);
    };
    load();
  }, [orgId]);

  if (loading) return <div className="text-sm text-gray-400">Loading farms...</div>;

  return (
    <div>
      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
        <Tractor className="w-4 h-4" /> Farms ({farms.length})
      </h4>
      {farms.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {farms.slice(0, 15).map(f => (
            <span key={f.farm_id} className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded-full">
              {f.farm_name}
            </span>
          ))}
          {farms.length > 15 && (
            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">+{farms.length - 15} more</span>
          )}
        </div>
      ) : (
        <p className="text-sm text-gray-500">No farms linked yet. Assign users first — their farms will be auto-linked.</p>
      )}
    </div>
  );
};

// ─── Create Org Modal ────────────────────────────────────────────────────────
const CreateOrgModal = ({ orgTypes, allUsers, onClose, onCreated }) => {
  const [form, setForm] = useState({
    name_org: '', org_type: 2, email: '', contact: '', company_registration_no: ''
  });
  const [adminMode, setAdminMode] = useState('existing'); // 'existing' | 'new'
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [newUser, setNewUser] = useState({ email: '', name_first: '', name_last: '', phone: '' });
  const [userSearch, setUserSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const filteredUsers = useMemo(() => {
    if (!userSearch.trim()) return allUsers.slice(0, 50);
    const q = userSearch.toLowerCase();
    return allUsers.filter(u =>
      u.email?.toLowerCase().includes(q) ||
      u.name_first?.toLowerCase().includes(q) ||
      u.name_last?.toLowerCase().includes(q)
    ).slice(0, 50);
  }, [allUsers, userSearch]);

  const handleCreate = async () => {
    if (!form.name_org.trim()) { alert('Organization name is required'); return; }
    if (adminMode === 'existing' && !selectedUserId) { alert('Please select a Company Admin user'); return; }
    if (adminMode === 'new' && !newUser.email.trim()) { alert('Company Admin email is required'); return; }

    setSaving(true);
    try {
      const params = {
        p_name_org: form.name_org.trim(),
        p_org_type: form.org_type,
        p_email: form.email.trim() || null,
        p_contact: form.contact.trim() || null,
        p_company_registration_no: form.company_registration_no.trim() || null,
        p_comp_admin_user_id: adminMode === 'existing' ? selectedUserId : null,
        p_new_user_email: adminMode === 'new' ? newUser.email.trim() : null,
        p_new_user_first_name: adminMode === 'new' ? newUser.name_first.trim() || null : null,
        p_new_user_last_name: adminMode === 'new' ? newUser.name_last.trim() || null : null,
        p_new_user_phone: adminMode === 'new' ? newUser.phone.trim() || null : null,
      };

      const { data, error } = await supabase.rpc('create_org_with_comp_admin', params);
      if (error) throw error;

      // If new user, create auth account with temp password
      if (adminMode === 'new' && data?.user_id) {
        const tempPassword = 'Temp' + Math.random().toString(36).substring(2, 8) + '!';
        // Store temp password for display
        await supabase.from('user').update({ temporary_password: tempPassword }).eq('id', data.user_id);
        alert(`Organization created!\n\nCompany Admin account:\nEmail: ${newUser.email}\nTemp Password: ${tempPassword}\n\n(User must sign up via the platform with this email)`);
      } else {
        alert('Organization created successfully!');
      }

      onClose();
      onCreated();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-8">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">Create Organization</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Org Fields */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name *</label>
            <input type="text" value={form.name_org} onChange={e => setForm({...form, name_org: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" placeholder="Enter organization name" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select value={form.org_type} onChange={e => setForm({...form, org_type: parseInt(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                {orgTypes.map(t => <option key={t.id} value={t.id}>{t.name_org_type} ({t.name_org_type3})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reg No.</label>
              <input type="text" value={form.company_registration_no} onChange={e => setForm({...form, company_registration_no: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="1234567-A" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="org@email.com" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact</label>
              <input type="text" value={form.contact} onChange={e => setForm({...form, contact: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="012-3456789" />
            </div>
          </div>

          {/* Company Admin Section */}
          <div className="border-t pt-4">
            <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-blue-600" />
              Company Admin (Required)
            </h4>
            <div className="flex gap-2 mb-3">
              <button onClick={() => setAdminMode('existing')}
                className={`flex-1 py-2 text-sm rounded-lg border ${adminMode === 'existing' ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-300 text-gray-600'}`}>
                Select Existing User
              </button>
              <button onClick={() => setAdminMode('new')}
                className={`flex-1 py-2 text-sm rounded-lg border ${adminMode === 'new' ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-300 text-gray-600'}`}>
                Register New User
              </button>
            </div>

            {adminMode === 'existing' ? (
              <div>
                <div className="relative mb-2">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="text" value={userSearch} onChange={e => setUserSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Search by name or email..." />
                </div>
                <div className="max-h-40 overflow-y-auto border rounded-lg">
                  {filteredUsers.map(u => (
                    <label key={u.id} className={`flex items-center gap-2 p-2 cursor-pointer text-sm hover:bg-gray-50 ${selectedUserId === u.id ? 'bg-blue-50' : ''}`}>
                      <input type="radio" name="comp_admin" checked={selectedUserId === u.id}
                        onChange={() => setSelectedUserId(u.id)} className="text-blue-600" />
                      <span className="font-medium">{[u.name_first, u.name_last].filter(Boolean).join(' ') || 'No name'}</span>
                      <span className="text-gray-400">{u.email}</span>
                    </label>
                  ))}
                </div>
                {selectedUserId && (
                  <p className="text-xs text-green-600 mt-1">
                    Selected: {allUsers.find(u => u.id === selectedUserId)?.email}
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Email *</label>
                  <input type="email" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="admin@company.com" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">First Name</label>
                    <input type="text" value={newUser.name_first} onChange={e => setNewUser({...newUser, name_first: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Last Name</label>
                    <input type="text" value={newUser.name_last} onChange={e => setNewUser({...newUser, name_last: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Phone</label>
                  <input type="text" value={newUser.phone} onChange={e => setNewUser({...newUser, phone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="012-3456789" />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-3 p-4 border-t bg-gray-50 rounded-b-xl">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={handleCreate} disabled={saving}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2">
            <Check className="w-4 h-4" />{saving ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Edit Org Modal ──────────────────────────────────────────────────────────
const EditOrgModal = ({ org, orgTypes, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name_org: org.name_org || '',
    org_type: org.org_type || 2,
    email: org.email || '',
    contact: org.contact || '',
    company_registration_no: org.company_registration_no || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name_org.trim()) { alert('Name required'); return; }
    setSaving(true);
    try {
      const { error } = await supabase.functions.invoke('update-season', {
        body: { seasonId: org.id, updates: {} } // We need a generic update function
      });
      // Use direct update instead — the org table may have different RLS
      const { error: updateError } = await supabase
        .from('organisation')
        .update({
          name_org: form.name_org.trim(),
          org_type: form.org_type,
          email: form.email.trim() || null,
          contact: form.contact.trim() || null,
          company_registration_no: form.company_registration_no.trim() || null
        })
        .eq('id', org.id);
      
      if (updateError) {
        // Try via RPC if RLS blocks
        const { error: rpcError } = await supabase.rpc('update_organisation', {
          p_org_id: org.id,
          p_name_org: form.name_org.trim(),
          p_org_type: form.org_type,
          p_email: form.email.trim() || null,
          p_contact: form.contact.trim() || null,
          p_company_registration_no: form.company_registration_no.trim() || null
        });
        if (rpcError) throw rpcError;
      }
      
      onClose();
      onSaved();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">Edit Organization</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input type="text" value={form.name_org} onChange={e => setForm({...form, name_org: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select value={form.org_type} onChange={e => setForm({...form, org_type: parseInt(e.target.value)})}
              className="w-full px-3 py-2 border rounded-lg">
              {orgTypes.map(t => <option key={t.id} value={t.id}>{t.name_org_type} ({t.name_org_type3})</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact</label>
              <input type="text" value={form.contact} onChange={e => setForm({...form, contact: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reg No.</label>
            <input type="text" value={form.company_registration_no} onChange={e => setForm({...form, company_registration_no: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg" />
          </div>
        </div>
        <div className="flex justify-end gap-3 p-4 border-t bg-gray-50 rounded-b-xl">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2">
            <Check className="w-4 h-4" />{saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Assign Users Modal (Admin) ──────────────────────────────────────────────
const AssignUsersModal = ({ org, allUsers, onClose, onSaved }) => {
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load currently assigned users
  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.rpc('get_org_users_with_roles', { p_org_id: org.id });
      // Pre-select currently assigned users (exclude comp_admin — they're permanent)
      setSelectedUserIds((data || []).filter(u => u.role !== 'comp_admin').map(u => u.user_id));
      setLoading(false);
    };
    load();
  }, [org.id]);

  const compAdminId = org.compAdmin?.user_id;

  const filteredUsers = useMemo(() => {
    let list = allUsers.filter(u => u.id !== compAdminId); // Exclude comp_admin from list
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(u => u.email?.toLowerCase().includes(q) || u.name_first?.toLowerCase().includes(q) || u.name_last?.toLowerCase().includes(q));
    }
    return list.slice(0, 100); // Limit to 100 for performance
  }, [allUsers, search, compAdminId]);

  const toggleUser = (userId) => {
    setSelectedUserIds(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Prefer the fixed v3 function. Fall back to v2 only if v3 is not
      // deployed yet so production keeps working during the rollout.
      let { error } = await supabase.rpc('assign_users_to_org_v3', {
        p_org_id: org.id,
        p_user_ids: selectedUserIds,
      });
      if (error && /not\s+find\s+the\s+function|function\s+.*does\s+not\s+exist|PGRST202/i.test(error.message || '')) {
        const legacy = await supabase.rpc('assign_users_to_org_v2', {
          p_org_id: org.id,
          p_user_ids: selectedUserIds,
        });
        error = legacy.error;
      }
      if (error) {
        // The known broken-schema error in v2 — surface the migration hint so
        // the admin knows this needs the SQL fix in supabase/migrations/20260507_*.
        if (/f\.created_by|column .*created_by.*does not exist/i.test(error.message || '')) {
          throw new Error(
            'Database needs a one-time SQL fix. Open Supabase → SQL Editor and ' +
            'run supabase/migrations/20260507_fix_assign_users_to_org.sql. ' +
            'Original error: ' + error.message,
          );
        }
        throw error;
      }
      onSaved();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="text-lg font-semibold">Assign Users to {org.name_org}</h3>
            <p className="text-xs text-gray-500 mt-1">Selected users' farms will be auto-linked to the organization</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        
        {/* Comp Admin badge */}
        {org.compAdmin && (
          <div className="px-4 pt-3">
            <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg text-sm">
              <ShieldCheck className="w-4 h-4 text-blue-600" />
              <span className="text-blue-800">Company Admin: <strong>{org.compAdmin.name_first || org.compAdmin.email}</strong> (permanent)</span>
            </div>
          </div>
        )}

        <div className="p-4 border-b">
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg" placeholder="Search users by name or email..." />
          </div>
          <p className="text-sm text-gray-500 mt-2">{selectedUserIds.length} members selected</p>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <p className="text-gray-500 text-center py-4">Loading...</p>
          ) : (
            <div className="space-y-1">
              {filteredUsers.map(u => {
                const isSelected = selectedUserIds.includes(u.id);
                const displayName = [u.name_first, u.name_last].filter(Boolean).join(' ') || u.email;
                return (
                  <label key={u.id} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${isSelected ? 'bg-purple-50 border border-purple-200' : 'hover:bg-gray-50 border border-transparent'}`}>
                    <input type="checkbox" checked={isSelected} onChange={() => toggleUser(u.id)}
                      className="w-4 h-4 text-purple-600 rounded" />
                    <Users className={`w-4 h-4 ${isSelected ? 'text-purple-600' : 'text-gray-400'}`} />
                    <span className={isSelected ? 'text-purple-900 font-medium' : 'text-gray-700'}>{displayName}</span>
                    {u.email && u.name_first && <span className="text-xs text-gray-400">({u.email})</span>}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-4 border-t bg-gray-50 rounded-b-xl">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2">
            <Check className="w-4 h-4" />{saving ? 'Saving...' : `Save (${selectedUserIds.length} users)`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrganizationManagement;
